import { BookingV4, Preorder, Item, Option } from "../../types/bookings";
import { postUpdateBookingV4 } from "./UpdateBooking";

const generateId = () => {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

export type PreOrderItemFormData = {
  itemId: string;
  itemName: string;
  itemPrice: number;
  itemType: string;
  itemSubType: string;
  forName?: string;
  seat?: string;
  time?: string;
  quantity: number;
  discountType?: "percentage" | "fixed";
  discountAmount?: number;
  description?: string;
  options?: Option[];
  optionsPrice?: number;
};

type ItemPricing = {
  discountValue: number;
  optionsPrice: number;
  priceWithOptions: number;
  totalWithOptions: number;
  optionsString?: string;
};

const calculateItemPricing = (itemData: PreOrderItemFormData): ItemPricing => {
  const baseTotal = itemData.quantity * itemData.itemPrice;
  let discountValue = 0;

  if (itemData.discountAmount) {
    if (itemData.discountType === "percentage") {
      discountValue = (baseTotal * itemData.discountAmount) / 100;
    } else {
      discountValue = itemData.discountAmount;
    }
  }

  const optionsPrice = itemData.optionsPrice ?? 0;
  const priceWithOptions = itemData.itemPrice + optionsPrice;
  const baseTotalWithOptions = itemData.quantity * priceWithOptions;
  const totalWithOptions = baseTotalWithOptions - discountValue;

  const optionsString = itemData.options
    ?.flatMap((o) => o.values.map((v) => v.label))
    .join(", ");

  return {
    discountValue,
    optionsPrice,
    priceWithOptions,
    totalWithOptions,
    optionsString
  };
};

const buildItemFields = (
  itemData: PreOrderItemFormData,
  pricing: ItemPricing
): Partial<Item> => ({
  name: itemData.itemName,
  quantity: itemData.quantity,
  total: pricing.totalWithOptions,
  discount_amount: itemData.discountAmount,
  discount_type: itemData.discountType,
  discount_value: pricing.discountValue,
  type: itemData.itemType,
  sub_type: itemData.itemSubType,
  time: itemData.time,
  price: pricing.priceWithOptions,
  base_price: itemData.itemPrice,
  options_price: pricing.optionsPrice,
  for_name: itemData.forName ?? "",
  seat: itemData.seat,
  options: itemData.options,
  options_string: pricing.optionsString,
  description: itemData.description
});

const saveBookingPreOrders = async (
  booking: BookingV4,
  updatedPreOrders: Preorder[] | undefined,
  errorMessage: string
): Promise<{ booking?: BookingV4; error?: string }> => {
  const updatedBooking: BookingV4 = {
    ...booking,
    preorders: updatedPreOrders
  };

  const savedBooking = await postUpdateBookingV4(updatedBooking);

  if (!savedBooking) {
    return { error: errorMessage };
  }

  return { booking: savedBooking };
};

/**
 * Add a new pre-order item to a booking
 */
export const addPreOrderItem = async (
  booking: BookingV4,
  itemData: PreOrderItemFormData
): Promise<{ booking?: BookingV4; error?: string }> => {
  try {
    const pricing = calculateItemPricing(itemData);

    const newItem: Item = {
      id: generateId(),
      ...buildItemFields(itemData, pricing),
      plu: 0,
      items: [],
      surcharge: null
    } as Item;

    const newPreOrder: Preorder = {
      id: generateId(),
      created_date: new Date(),
      name: "Admin",
      email: booking.email ?? "",
      items: [newItem],
      total_cost: pricing.totalWithOptions,
      amount_paid: 0,
      source: "Admin",
      status: "complete",
      service_charge_percentage: 0,
      service_charge_amount: 0,
      total_discount: pricing.discountValue
    };

    const updatedPreOrders = [...(booking.preorders || []), newPreOrder];

    return await saveBookingPreOrders(
      booking,
      updatedPreOrders,
      "Failed to save pre-order item"
    );
  } catch (error) {
    console.error("Error adding pre-order item:", error);
    return { error: "Failed to add pre-order item" };
  }
};

/**
 * Update an existing pre-order item
 */
export const updatePreOrderItem = async (
  booking: BookingV4,
  preOrderId: string,
  itemData: PreOrderItemFormData
): Promise<{ booking?: BookingV4; error?: string }> => {
  try {
    const pricing = calculateItemPricing(itemData);

    const updatedPreOrders = booking.preorders?.map((preOrder) => {
      if (preOrder.id === preOrderId) {
        const updatedItem: Item = {
          ...preOrder.items[0],
          ...buildItemFields(itemData, pricing)
        } as Item;

        return {
          ...preOrder,
          items: [updatedItem],
          total_cost: pricing.totalWithOptions,
          total_discount: pricing.discountValue
        };
      }
      return preOrder;
    });

    return await saveBookingPreOrders(
      booking,
      updatedPreOrders,
      "Failed to update pre-order item"
    );
  } catch (error) {
    console.error("Error updating pre-order item:", error);
    return { error: "Failed to update pre-order item" };
  }
};

/**
 * Delete a pre-order item
 */
export const deletePreOrderItem = async (
  booking: BookingV4,
  preOrderId: string
): Promise<{ booking?: BookingV4; error?: string }> => {
  try {
    const updatedPreOrders = booking.preorders?.map((preOrder) => {
      if (preOrder.id === preOrderId) {
        return {
          ...preOrder,
          status: "deleted"
        };
      }
      return preOrder;
    });

    return await saveBookingPreOrders(
      booking,
      updatedPreOrders,
      "Failed to delete pre-order item"
    );
  } catch (error) {
    console.error("Error deleting pre-order item:", error);
    return { error: "Failed to delete pre-order item" };
  }
};

/**
 * Update service charge for a specific pre-order
 */
export const updateServiceCharge = async (
  booking: BookingV4,
  preOrderId: string,
  serviceChargePercentage: number
): Promise<{ booking?: BookingV4; error?: string }> => {
  try {
    const updatedPreOrders = booking.preorders?.map((preOrder) => {
      if (preOrder.id === preOrderId) {
        const serviceChargeAmount =
          (preOrder.total_cost * serviceChargePercentage) / 100;

        return {
          ...preOrder,
          service_charge_percentage: serviceChargePercentage,
          service_charge_amount: serviceChargeAmount
        };
      }
      return preOrder;
    });

    return await saveBookingPreOrders(
      booking,
      updatedPreOrders,
      "Failed to update service charge"
    );
  } catch (error) {
    console.error("Error updating service charge:", error);
    return { error: "Failed to update service charge" };
  }
};

/**
 * Update customer notes for a specific pre-order
 */
export const updatePreOrderComments = async (
  booking: BookingV4,
  preOrderId: string,
  comments: string
): Promise<{ booking?: BookingV4; error?: string }> => {
  try {
    const updatedPreOrders = booking.preorders?.map((preOrder) => {
      if (preOrder.id === preOrderId) {
        return {
          ...preOrder,
          customer_notes: comments
        };
      }
      return preOrder;
    });

    return await saveBookingPreOrders(
      booking,
      updatedPreOrders,
      "Failed to update pre-order comments"
    );
  } catch (error) {
    console.error("Error updating pre-order comments:", error);
    return { error: "Failed to update pre-order comments" };
  }
};
